import { useRouter, type RouteRecordNormalized } from 'vue-router'
import {
  useBreadcrumb as useKstoneBreadcrumb,
  setBreadcrumbLabel,
  clearBreadcrumbLabel,
  type BreadcrumbConfig,
  type BreadcrumbItem
} from '@siclop/kstone-vue'

/**
 * Tables du fil d'Ariane de kstone-vue, construites à partir de `meta.breadcrumb` des routes
 * (`{ label, parent }`, déclarées dans les `routes.ts` des modules) :
 * - libellé de chaque route qui en déclare un ;
 * - route parente ;
 * - chemin des routes sans paramètre (un parent ne devient un lien que s'il en a un, ou si
 *   la vue a fourni un chemin par `setBreadcrumbLabel`).
 */
export function breadcrumbConfigFromRoutes(
  routes: Pick<RouteRecordNormalized, 'name' | 'path' | 'meta' | 'aliasOf'>[]
): Pick<BreadcrumbConfig, 'routeLabels' | 'parentRoutes' | 'routePaths'> {
  const routeLabels: Record<string, string> = {}
  const parentRoutes: Record<string, string> = {}
  const routePaths: Record<string, string> = {}

  for (const route of routes) {
    if (typeof route.name !== 'string' || route.aliasOf) continue
    const breadcrumb = route.meta.breadcrumb
    if (breadcrumb) {
      routeLabels[route.name] = breadcrumb.label
      if (breadcrumb.parent) parentRoutes[route.name] = breadcrumb.parent
    }
    if (!route.path.includes(':')) routePaths[route.name] = route.path
  }

  return { routeLabels, parentRoutes, routePaths }
}

/**
 * Composable for generating breadcrumbs from route.
 * Delegates to kstone-vue's useBreadcrumb to share the same reactive state
 * as upstream package views (ProductDetailView, etc.) that call
 * setBreadcrumbLabel from @siclop/kstone-vue.
 */
export function useBreadcrumb() {
  return useKstoneBreadcrumb(breadcrumbConfigFromRoutes(useRouter().getRoutes()))
}

export { setBreadcrumbLabel, clearBreadcrumbLabel }

export type { BreadcrumbItem }
